import React from 'react';
import { Ban, Eye, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DialogTrigger } from 'react-aria-components';
import { Pressable } from '@react-aria/interactions';
import { deactivatePhysicianOptions, reactivatePhysicianOptions } from '@generated';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { Prompt } from '~/shared/components/Prompt';
import { Popover } from '~/shared/components/Popover';
import { RenderAddressCell } from '~/shared/data-tables/column-types/AddressColumn';
import { ActiveColumnCell } from '~/shared/data-tables/column-types/ActiveColumn';
import { formatToShortDateAndTimeOrNull } from '~/util/date';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';

const PhysiciansDataTable = createQueryDataTable('physician');
type RowType = RowOfQueryDataTable<typeof PhysiciansDataTable>;
type TableProps = PropsOfQueryDataTable<typeof PhysiciansDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const deactivatePhysician = useMutation({
		...deactivatePhysicianOptions,
		invalidateQueryKeys: [getSearchQueryKey('physician')]
	});

	const reactivatePhysician = useMutation({
		...reactivatePhysicianOptions,
		invalidateQueryKeys: [getSearchQueryKey('physician')]
	});

	return (
		<TableActions>
			{row.active ? (
				<>
					<TableActions.ViewButton aria-label="Edit Physician" onClick={() => navigate(`edit/${row.id}`)} />
					<Button
						Icon={Ban}
						aria-label="Deactivate Physician"
						intent="destructive"
						appearance="ghost"
						renderModal={closeModal => (
							<Prompt
								title="Deactivate Physician"
								text="Are you sure you want to deactivate this pharmacy?"
								onAction={() => {
									deactivatePhysician.mutateAsync({ pharmacyId: row.id });
									closeModal();
								}}
								onCancel={closeModal}
							/>
						)}
					/>
				</>
			) : (
				<Button
					Icon={Eye}
					aria-label="Reactivate Physician"
					appearance="ghost"
					renderModal={closeModal => (
						<Prompt
							title="Reactivate Physician"
							text="Are you sure you want to reactivate this pharmacy?"
							onAction={() => {
								reactivatePhysician.mutateAsync({ pharmacyId: row.id });
								closeModal();
							}}
							onCancel={closeModal}
						/>
					)}
				/>
			)}
		</TableActions>
	);
}

function PhysicianNameCell({ row }: { row: RowType }) {
	return (
		<div className="flex flex-col">
			<div className="flex items-center gap-2">
				<span className="text-sm font-semibold text-gray-900">{row.name}</span>
			</div>
			{row.notes && <p className="mt-1 max-w-xs truncate text-xs text-gray-500">{row.notes}</p>}
		</div>
	);
}

function PecosVerifiedCell({ row }: { row: RowType }) {
	return (
		<div className="flex flex-row items-center justify-center gap-2 text-center">
			{!row.pecosEnrolled ? (
				'No'
			) : (
				<>
					Yes
					<DialogTrigger>
						<Pressable>
							<Info className="size-5 cursor-pointer text-gray-500" />
						</Pressable>

						<Popover showArrow className="max-w-xs p-4">
							Last checked on: {formatToShortDateAndTimeOrNull(row.pecosLastCheckedOnUtc)}
						</Popover>
					</DialogTrigger>
				</>
			)}
		</div>
	);
}

const columnDefinitions = PhysiciansDataTable.columns([
	{
		header: 'Physician Name',
		key: 'name',
		sortable: true,
		renderCell: row => <PhysicianNameCell row={row} />
	},
	{
		header: 'Address',
		key: 'addressOne',
		width: 'w-[200px]',
		renderCell: row => (
			<RenderAddressCell
				address={{ addressOne: row.addressOne ?? '', city: row.city ?? '', state: row.state ?? '', zipCode: row.zipCode ?? '' }}
			/>
		)
	},
	{
		header: 'Phone Number',
		key: 'phoneNumber',
		width: 'w-[200px]',
		type: 'phone'
	},
	{
		header: 'Fax Number',
		key: 'faxNumber',
		width: 'w-[200px]',
		type: 'phone'
	},
	{
		header: 'NPI Number',
		key: 'npiNumber',
		sortable: true,
		width: 'w-[130px]'
	},
	{
		header: 'PECOS Verified',
		key: 'pecosEnrolled',
		width: 'w-[130px]',
		renderCell: row => <PecosVerifiedCell row={row} />
	},
	{
		header: 'Active',
		key: 'active',
		width: 'w-[100px]',
		renderCell: row => <ActiveColumnCell active={row.active} />
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function PhysicianTable(props: TableProps) {
	return (
		<PhysiciansDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No physicians found."
			getMuteRowOpacity={row => !row.active}
			{...props}
		/>
	);
}
